import React, { useEffect, useState } from 'react';
import API from '../services/api';
import { SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, requestPasswordReset } from '../services/passwordReset';
import DeleteAccount from '../components/DeleteAccount';
import Legal from './Legal';
import { CONNEXION_PRETE } from './ConnexionCompteur';

// Page « Mon compte » : profil, énergie, abonnement, notifications, sécurité, données.
const C = {
  card: '#0D1117', lime: '#C8F135', text: '#EEF2FF', muted: 'rgba(238,242,255,0.45)',
  border: 'rgba(238,242,255,0.07)', green: '#4ade80', amber: '#f59e0b', red: '#ff6b6b',
  title: { fontFamily: "'Lexend', sans-serif" }, body: { fontFamily: "'Plus Jakarta Sans', sans-serif" },
};
const CONTACT = 'contact@nowatt.app';

// Règle : seul ce qui fonctionne entièrement est affiché. On passe un drapeau à true quand la fonction est prête.
const READY = {
  notifications: false,  // les envois d'alertes ne sont pas encore construits
  enedis: CONNEXION_PRETE, // parcours Data Connect : voir ConnexionCompteur.js
  biometrie: false,      // empreinte / Face ID : app mobile
  legal: false,          // nouveaux textes CGU et confidentialité à valider et compléter (SIREN, médiateur)
  contact: false,        // boîte contact@nowatt.app à vérifier
};

const dateFr = (v) => {
  if (!v) return null;
  const d = typeof v === 'number' ? new Date(v * 1000) : new Date(v);
  return isNaN(d) ? null : d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
};
const monthFr = (v) => { const d = v ? new Date(v) : null; return d && !isNaN(d) ? d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }) : null; };
const initials = (name, email) => {
  const src = (name || '').trim() || (email || '').split('@')[0];
  const parts = src.split(/[\s._-]+/).filter(Boolean);
  return ((parts[0] || '?')[0] + (parts[1] ? parts[1][0] : '')).toUpperCase();
};

// Lecture et mise à jour du compte de connexion (nom, préférences), directement chez Supabase
const authHeaders = () => ({
  'Content-Type': 'application/json',
  apikey: SUPABASE_PUBLISHABLE_KEY,
  Authorization: `Bearer ${localStorage.getItem('nowatt_token')}`,
});
async function fetchProfile() {
  const res = await fetch(`${SUPABASE_URL}/auth/v1/user`, { headers: authHeaders() });
  if (!res.ok) throw new Error('PROFILE');
  return res.json();
}
async function updateMetadata(data) {
  const res = await fetch(`${SUPABASE_URL}/auth/v1/user`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ data }) });
  if (!res.ok) throw new Error('UPDATE');
  return res.json();
}

// ── Éléments de mise en page ──
const Section = ({ title, children }) => (
  <section style={{ marginBottom: 6 }}>
    <h3 style={{ fontSize: '0.68rem', letterSpacing: 1, textTransform: 'uppercase', color: C.muted, margin: '22px 4px 8px', fontWeight: 600, ...C.body }}>{title}</h3>
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: 'hidden' }}>{children}</div>
  </section>
);
const Row = ({ label, children, strong, dim, danger, onClick, first }) => {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag onClick={onClick} style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, width: '100%', boxSizing: 'border-box',
      padding: '13px 16px', fontSize: '0.84rem', background: 'none', border: 'none',
      borderTop: first ? 'none' : `1px solid ${C.border}`, textAlign: 'left', color: C.text,
      cursor: onClick ? 'pointer' : 'default', opacity: dim ? 0.55 : 1, ...C.body,
    }}>
      <span style={{ color: danger ? C.red : strong ? C.text : C.muted, minWidth: 0 }}>{label}</span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 8, textAlign: 'right', flexShrink: 0, maxWidth: '62%' }}>{children}</span>
    </Tag>
  );
};
const Pill = ({ children, on }) => (
  <span style={{ fontSize: '0.68rem', padding: '3px 8px', borderRadius: 20, background: on ? 'rgba(200,241,53,0.12)' : 'rgba(238,242,255,0.06)', color: on ? C.lime : C.muted, whiteSpace: 'nowrap' }}>{children}</span>
);
const Chevron = ({ color = C.lime, label }) => <span style={{ color, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{label ? `${label} ›` : '›'}</span>;
const Toggle = ({ on, onChange, label }) => (
  <button role="switch" aria-checked={on} aria-label={label} onClick={onChange} style={{ width: 42, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer', background: on ? C.lime : 'rgba(238,242,255,0.15)', position: 'relative', transition: 'background .2s', flexShrink: 0 }}>
    <span style={{ position: 'absolute', top: 3, left: on ? 21 : 3, width: 18, height: 18, borderRadius: '50%', background: on ? '#06080C' : '#EEF2FF', transition: 'left .2s' }} />
  </button>
);
const smallBtn = { background: 'none', border: '1px solid rgba(238,242,255,0.15)', color: 'rgba(238,242,255,0.75)', borderRadius: 8, padding: '7px 12px', fontSize: '0.78rem', cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif" };
const limeBtn = { ...smallBtn, background: C.lime, color: '#06080C', border: 'none', fontWeight: 700 };
const Note = ({ children, error }) => <p role={error ? 'alert' : 'status'} style={{ margin: 0, padding: '0 16px 12px', fontSize: '0.76rem', color: error ? C.red : C.muted, lineHeight: 1.5, ...C.body }}>{children}</p>;

export default function Account({ onConnectMeter, reco, subStatus, setSubStatus, onSubscribe, onEditEnergy, onAccountDeleted, isDesktop }) {
  const [profile, setProfile] = useState(null);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [msg, setMsg] = useState({});           // messages par bloc : { profil, abo, notif, secu, data }
  const [busy, setBusy] = useState('');
  const [invoices, setInvoices] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [legal, setLegal] = useState(null);     // 'cgu' | 'privacy'

  useEffect(() => { fetchProfile().then(setProfile).catch(() => {}); }, []);

  const say = (key, text, error) => setMsg((m) => ({ ...m, [key]: text ? { text, error } : null }));
  const meta = profile?.user_metadata || {};
  const name = meta.full_name || '';
  const email = profile?.email || '';
  const prefs = { alerts: true, monthly: false, ...(meta.preferences?.notifications || {}) };

  const subscribed = !!subStatus?.subscribed;
  const ending = subscribed && subStatus?.cancel_at_period_end;
  const until = dateFr(subStatus?.period_end);
  const savings = Math.round(reco?.annual_savings || 0);

  if (legal) return <Legal initialTab={legal} onBack={() => setLegal(null)} />;

  // ── Actions ──
  const saveName = async () => {
    const v = nameDraft.trim();
    if (!v) { say('profil', 'Le nom ne peut pas être vide.', true); return; }
    setBusy('name');
    try { setProfile(await updateMetadata({ full_name: v })); setEditingName(false); say('profil', null); }
    catch { say('profil', 'Impossible d\'enregistrer pour le moment. Réessayez dans un instant.', true); }
    setBusy('');
  };
  const togglePref = async (key) => {
    const next = { ...prefs, [key]: !prefs[key] };
    setProfile((p) => ({ ...p, user_metadata: { ...(p?.user_metadata || {}), preferences: { ...(p?.user_metadata?.preferences || {}), notifications: next } } }));
    try { await updateMetadata({ preferences: { ...(meta.preferences || {}), notifications: next } }); say('notif', null); }
    catch { say('notif', 'Préférence non enregistrée. Réessayez dans un instant.', true); fetchProfile().then(setProfile).catch(() => {}); }
  };
  const subscriptionCall = async (path) => {
    setBusy('abo'); say('abo', null);
    try { const r = await API.post(path); setSubStatus({ ...subStatus, ...r.data, subscribed: true }); setConfirmCancel(false); }
    catch (e) { say('abo', e.response?.data?.detail || 'Une erreur est survenue. Réessayez dans un instant.', true); }
    setBusy('');
  };
  const loadInvoices = async () => {
    if (invoices) { setInvoices(null); return; }
    setBusy('inv');
    try { setInvoices((await API.get('/stripe/invoices')).data.invoices || []); }
    catch { say('abo', 'Impossible de récupérer vos factures pour le moment.', true); }
    setBusy('');
  };
  const sendPasswordLink = async () => {
    if (!email) return;
    setBusy('pwd');
    try { await requestPasswordReset(email); say('secu', `Un lien pour choisir un nouveau mot de passe vient d'être envoyé à ${email}.`); }
    catch (e) { say('secu', e.message === 'RATE_LIMIT' ? 'Un lien vient déjà d\'être envoyé. Patientez une minute avant de réessayer.' : 'Envoi impossible pour le moment. Réessayez dans un instant.', true); }
    setBusy('');
  };
  const exportData = async () => {
    setBusy('export');
    try {
      const { data } = await API.get('/account/export');
      const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }));
      const a = document.createElement('a'); a.href = url; a.download = 'mes-donnees-nowatt.json'; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      say('data', 'Le fichier de vos données a été téléchargé.');
    } catch { say('data', 'Export impossible pour le moment. Réessayez dans un instant.', true); }
    setBusy('');
  };

  const col = { minWidth: 0 };
  return (
    <div style={{ maxWidth: 760 }}>
      <h2 style={{ margin: '0 0 16px', fontSize: '1.3rem', fontWeight: 700, ...C.title }}>Mon compte</h2>

      {/* En-tête */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, background: 'linear-gradient(135deg,#0d1a00,#081000)', border: '1px solid rgba(200,241,53,0.22)', borderRadius: 16, padding: 16, marginBottom: 4 }}>
        <div aria-hidden="true" style={{ width: 52, height: 52, borderRadius: '50%', background: 'rgba(200,241,53,0.12)', color: C.lime, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '1.05rem', flexShrink: 0, ...C.title }}>{profile ? initials(name, email) : ''}</div>
        <div style={{ minWidth: 0 }}>
          <p style={{ margin: 0, fontWeight: 700, fontSize: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', ...C.title }}>{name || email || ' '}</p>
          {monthFr(profile?.created_at) && <p style={{ margin: '3px 0 0', fontSize: '0.75rem', color: C.muted, ...C.body }}>Membre depuis {monthFr(profile.created_at)}</p>}
        </div>
        {savings > 0 && (
          <div style={{ marginLeft: 'auto', textAlign: 'right', flexShrink: 0 }}>
            <p style={{ margin: 0, color: C.lime, fontSize: '1.35rem', fontWeight: 700, ...C.title }}>{savings} €</p>
            <p style={{ margin: 0, fontSize: '0.68rem', color: C.muted, ...C.body }}>d'économie estimée<br />par an</p>
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isDesktop ? '1fr 1fr' : '1fr', gap: '0 18px' }}>
        <div style={col}>
          <Section title="Mon profil">
            {editingName ? (
              <div style={{ padding: '12px 16px', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <input aria-label="Nom" value={nameDraft} onChange={(e) => setNameDraft(e.target.value)} autoFocus
                  style={{ flex: '1 1 160px', background: 'rgba(238,242,255,0.04)', border: '1px solid rgba(238,242,255,0.15)', borderRadius: 8, padding: '8px 10px', color: C.text, fontSize: '0.85rem', ...C.body }} />
                <button onClick={() => setEditingName(false)} style={smallBtn}>Annuler</button>
                <button onClick={saveName} disabled={busy === 'name'} style={limeBtn}>{busy === 'name' ? '…' : 'Enregistrer'}</button>
              </div>
            ) : (
              <Row first label="Nom"><span>{name || '—'}</span><button onClick={() => { setNameDraft(name); setEditingName(true); }} style={{ background: 'none', border: 'none', color: C.lime, cursor: 'pointer', fontSize: '0.8rem', padding: 0, ...C.body }}>Modifier</button></Row>
            )}
            <Row label="Email"><span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{email || '—'}</span></Row>
            <Row label="Inscrit le"><span>{dateFr(profile?.created_at) || '—'}</span></Row>
            {msg.profil && <Note error={msg.profil.error}>{msg.profil.text}</Note>}
          </Section>

          <Section title="Mon énergie">
            <Row first label="Fournisseur actuel"><span>{reco?.current_supplier || 'Non renseigné'}</span></Row>
            <Row label="Consommation annuelle"><span style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{reco?.annual_kwh ? `${Number(reco.annual_kwh).toLocaleString('fr-FR')} kWh` : 'Non renseignée'}</span></Row>
            <Row label="Puissance retenue"><span>9 kVA · Base <span style={{ color: C.muted }}>(hypothèse)</span></span></Row>
            <Row label="Source des données"><Pill>Saisie manuelle</Pill></Row>
            <Row strong label="Mettre à jour mes infos" onClick={onEditEnergy}><Chevron label="Modifier" /></Row>
            {READY.enedis && <Row strong label="Connecter mon compteur Linky" onClick={onConnectMeter}><Chevron label="Connecter" /></Row>}
          </Section>

          <Section title="Mon abonnement">
            <Row first label="Statut"><span style={{ color: !subscribed ? C.muted : ending ? C.amber : C.green, fontWeight: 600 }}>{!subscribed ? 'Aucun abonnement' : ending ? 'Résilié' : 'Actif'}</span></Row>
            <Row label="Formule"><span>5,99 € / mois</span></Row>
            {subscribed && <Row label={ending ? 'Accès jusqu\'au' : 'Prochain renouvellement'}><span>{until || '—'}</span></Row>}
            {subscribed && <Row strong label="Mes factures" onClick={loadInvoices}><Chevron label={busy === 'inv' ? '…' : invoices ? 'Masquer' : 'Voir'} /></Row>}
            {invoices && (
              <div style={{ padding: '0 16px 10px' }}>
                {invoices.length === 0 && <p style={{ margin: 0, fontSize: '0.78rem', color: C.muted, ...C.body }}>Aucune facture pour le moment.</p>}
                {invoices.map((f, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', fontSize: '0.78rem', ...C.body }}>
                    <span style={{ color: C.muted }}>{dateFr(f.date)}</span>
                    <span>{f.amount.toFixed(2).replace('.', ',')} €</span>
                    {f.pdf || f.url ? <a href={f.pdf || f.url} target="_blank" rel="noopener noreferrer" style={{ color: C.lime, textDecoration: 'none' }}>PDF ›</a> : <span />}
                  </div>
                ))}
              </div>
            )}
            {!subscribed && <Row strong label="Débloquer le fournisseur recommandé"><button onClick={onSubscribe} style={limeBtn}>S'abonner</button></Row>}
            {ending && <Row strong label="Changer d'avis"><button onClick={() => subscriptionCall('/stripe/resume')} disabled={busy === 'abo'} style={limeBtn}>{busy === 'abo' ? '…' : 'Reprendre'}</button></Row>}
            {subscribed && !ending && !confirmCancel && <Row strong label="Résilier l'abonnement"><button onClick={() => setConfirmCancel(true)} style={smallBtn}>Résilier</button></Row>}
            {confirmCancel && (
              <div style={{ padding: '12px 16px', borderTop: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <p style={{ margin: 0, fontSize: '0.8rem', color: 'rgba(238,242,255,0.75)', lineHeight: 1.5, ...C.body }}>
                  Votre abonnement ne sera pas renouvelé. {until ? `Vous gardez l'accès à tout jusqu'au ${until}.` : 'Vous gardez l\'accès jusqu\'à la fin de la période payée.'} Vous pourrez le reprendre d'ici là.
                </p>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                  <button onClick={() => setConfirmCancel(false)} disabled={busy === 'abo'} style={smallBtn}>Garder mon abonnement</button>
                  <button onClick={() => subscriptionCall('/stripe/cancel')} disabled={busy === 'abo'} style={{ ...smallBtn, color: C.red, borderColor: 'rgba(255,68,68,0.35)' }}>{busy === 'abo' ? 'Résiliation…' : 'Confirmer la résiliation'}</button>
                </div>
              </div>
            )}
            {msg.abo && <Note error={msg.abo.error}>{msg.abo.text}</Note>}
          </Section>
        </div>

        <div style={col}>
          {READY.notifications && <Section title="Notifications">
            <Row first strong label="M'alerter quand une offre moins chère apparaît"><Toggle on={prefs.alerts} onChange={() => togglePref('alerts')} label="Alertes nouvelles offres" /></Row>
            <Row strong label="Récapitulatif mensuel par email"><Toggle on={prefs.monthly} onChange={() => togglePref('monthly')} label="Récapitulatif mensuel" /></Row>
            {msg.notif && <Note error={msg.notif.error}>{msg.notif.text}</Note>}
          </Section>}

          <Section title="Sécurité">
            <Row first strong label="Changer mon mot de passe" onClick={busy === 'pwd' ? undefined : sendPasswordLink}><Chevron label={busy === 'pwd' ? '…' : 'Recevoir un lien'} /></Row>
            {READY.biometrie && <Row dim label="Connexion par empreinte / Face ID"><Pill>Dans l'app mobile</Pill></Row>}
            {msg.secu && <Note error={msg.secu.error}>{msg.secu.text}</Note>}
          </Section>

          <Section title="Mes données">
            <Row first strong label="Télécharger mes données" onClick={busy === 'export' ? undefined : exportData}><Chevron label={busy === 'export' ? '…' : 'Télécharger'} /></Row>
            {READY.legal && <Row strong label="Conditions générales" onClick={() => setLegal('cgu')}><Chevron /></Row>}
            {READY.legal && <Row strong label="Politique de confidentialité" onClick={() => setLegal('privacy')}><Chevron /></Row>}
            {READY.contact && <Row strong label="Contacter Nowatt"><a href={`mailto:${CONTACT}`} style={{ color: C.lime, textDecoration: 'none', fontSize: '0.8rem' }}>{CONTACT}</a></Row>}
            {!showDelete && <Row danger label="Supprimer mon compte" onClick={() => setShowDelete(true)}><Chevron color={C.red} /></Row>}
            {msg.data && <Note error={msg.data.error}>{msg.data.text}</Note>}
            {showDelete && (
              <div style={{ padding: '0 16px 16px' }}>
                <DeleteAccount initiallyOpen subscribed={subscribed} onCancel={() => setShowDelete(false)} onDeleted={onAccountDeleted} />
              </div>
            )}
          </Section>
        </div>
      </div>

    </div>
  );
}
